import { AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, List, Search, Trash2 } from 'lucide-react'
import { type MouseEvent, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { UserLabel } from '@/components/ui/user-label'
import { isProduction } from '../../../../../config/config'
import { useDebouncedInput } from '../../../../../hooks/useDebouncedInput'
import { cn, formatRelativeDate } from '../../../../../lib/utils'
import {
  type BuyerType,
  LeadSource,
  type SalesLeadFilters,
  type SalesLeadListItem,
  salesLeadStatusColors,
  salesLeadStatusLabels,
  useDeleteLead,
  useSalesLeads,
  useSalesLeadStats,
} from '../../../../../models/salesLead'
import { useStaffUsers } from '../../../../../models/user'
import { IngestFromCabDialog } from './IngestFromCabDialog'
import { IngestFromCsvDialog } from './IngestFromCsvDialog'
import { LeadActionAutomationDialog } from './LeadActionAutomationDialog'
import {
  MoreFiltersDialog,
  type MoreFiltersState,
  OrderingSelect,
  RepSelect,
  StatusSelect,
} from './LeadFilters'
import { LeadScoreBadge } from './LeadScoreBadge'
import { OutboundCallAutomationDialog } from './OutboundCallAutomationDialog'
import { SalesLeadDetail } from './SalesLeadDetail'
import { SequenceIndicator } from './Sequence/SequenceIndicator'

function formatDaysUntilExpiration(days: number | null): { text: string; className: string } | null {
  if (days === null) return null
  if (days < 0) return { text: `Expired ${Math.abs(days)}d ago`, className: 'text-red-600 font-medium' }
  if (days === 0) return { text: 'Expires today', className: 'text-red-600 font-medium' }
  if (days <= 14) return { text: `${days}d left`, className: 'text-red-600 font-medium' }
  if (days <= 30) return { text: `${days}d left`, className: 'text-orange-600 font-medium' }
  if (days <= 60) return { text: `${days}d left`, className: 'text-yellow-600' }
  return { text: `${days}d`, className: 'text-muted-foreground' }
}

interface DeleteLeadButtonProps {
  leadId: string
  leadName: string
  onDelete: (id: string, name: string) => void | Promise<void>
}

function DeleteLeadButton({ leadId, leadName, onDelete }: DeleteLeadButtonProps) {
  return (
    <ConfirmationDialog
      title={`Delete ${leadName}?`}
      description="This permanently removes the lead and all its activities, follows, documents and call/SMS history. Any converted customer is preserved. Dev/staging only."
      confirmLabel="Delete lead"
      onConfirm={() => onDelete(leadId, leadName)}
    >
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={`Delete ${leadName}`}
        title="Delete lead"
        className="text-destructive hover:bg-destructive/10"
      >
        <Trash2 className="size-4" />
      </Button>
    </ConfirmationDialog>
  )
}

interface CompactLeadCardProps {
  lead: SalesLeadListItem
  selected: boolean
  onClick: () => void
  onDelete?: (id: string, name: string) => void | Promise<void>
  /** Adds the table's remaining columns — for the list page, where the card replaces a row. */
  detailed?: boolean
}

function CompactLeadCard({ lead, selected, onClick, onDelete, detailed }: CompactLeadCardProps) {
  const expiry = formatDaysUntilExpiration(lead.daysUntilExpiration)
  return (
    <div
      className={cn(
        'group hover:bg-ink-50 relative flex items-start transition',
        selected && 'bg-brand-100 hover:bg-brand-100'
      )}
    >
      <button type="button" onClick={onClick} className="min-w-0 flex-1 px-3 py-2 text-left">
        <div className="flex items-center gap-2">
          {lead.hasUnread && (
            <span className="size-2 shrink-0 rounded-full bg-blue-500" aria-label="Unread activity" />
          )}
          <span className="truncate font-medium">{lead.legalName}</span>
          <SequenceIndicator status={lead.sequenceStatus} />
          <LeadScoreBadge score={lead.conversionScore} />
          {lead.oosStatus && (
            <Badge variant="destructive" className="shrink-0 text-xs">
              <AlertTriangle className="mr-1 size-3" />
              OOS
            </Badge>
          )}
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs">
          <Badge className={cn(salesLeadStatusColors[lead.status], 'text-xs')}>
            {salesLeadStatusLabels[lead.status]}
          </Badge>
          {expiry ? (
            <span className={expiry.className}>{expiry.text}</span>
          ) : (
            <span className="text-muted-foreground">No expiry</span>
          )}
        </div>
        {detailed && (
          <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            <span>
              DOT: {lead.dotNumber} · {lead.state}
            </span>
            <span>· {lead.powerUnits} units</span>
            <span>· {formatRelativeDate(lead.lastActivityAt, 'No activity')}</span>
            {lead.assignedRepName && <span>· {lead.assignedRepName}</span>}
          </div>
        )}
      </button>
      {/* Shown outright below lg — a touchscreen has no hover, and the control is tappable regardless. */}
      {onDelete && (
        <div className="pt-2 pr-2 transition max-lg:opacity-100 lg:opacity-0 lg:group-hover:opacity-100 lg:focus-within:opacity-100">
          <DeleteLeadButton leadId={lead.id} leadName={lead.legalName} onDelete={onDelete} />
        </div>
      )}
    </div>
  )
}

function CompactLeadCardSkeleton() {
  return (
    <div className="px-3 py-2">
      <Skeleton className="h-4 w-32" />
      <div className="mt-2 flex items-center gap-2">
        <Skeleton className="h-4 w-16 rounded-full" />
        <Skeleton className="h-3 w-12" />
      </div>
    </div>
  )
}

function LeadRowSkeleton({ canDelete }: { canDelete: boolean }) {
  return (
    <TableRow>
      <TableCell>
        <Skeleton className="h-4 w-40" />
        <Skeleton className="mt-2 h-3 w-24" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-4 w-16" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-4 w-8" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-5 w-8 rounded-full" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-5 w-20 rounded-full" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-4 w-28" />
        <Skeleton className="mt-2 h-3 w-20" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-4 w-16" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-4 w-20" />
      </TableCell>
      {canDelete && (
        <TableCell>
          <Skeleton className="size-4" />
        </TableCell>
      )}
    </TableRow>
  )
}

function LeadTableSkeleton({ canDelete }: { canDelete: boolean }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="font-semibold">Company</TableHead>
          <TableHead className="font-semibold">Expires</TableHead>
          <TableHead className="font-semibold">Units</TableHead>
          <TableHead className="font-semibold">Score</TableHead>
          <TableHead className="font-semibold">Status</TableHead>
          <TableHead className="font-semibold">Contact</TableHead>
          <TableHead className="font-semibold">Last Activity</TableHead>
          <TableHead className="font-semibold">Rep</TableHead>
          {canDelete && <TableHead className="w-12" />}
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 8 }).map((_, i) => (
          <LeadRowSkeleton key={i} canDelete={canDelete} />
        ))}
      </TableBody>
    </Table>
  )
}

export function SalesLeads() {
  const { id: selectedId } = useParams()
  // The split view's list rail doesn't fit next to a lead on a phone, so it moves into a sheet.
  const [listOpen, setListOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const page = Number(searchParams.get('page') || '1')
  const search = searchParams.get('search') || ''
  const statusFilter = searchParams.get('status') || ''
  const expiringDays = searchParams.get('expiring') || ''
  const notExpiringDays = searchParams.get('not_expiring') || ''
  const assignedRep = searchParams.get('rep') || ''
  const source = searchParams.get('source') || ''
  const stateFilter = searchParams.get('state') || ''
  const minScore = searchParams.get('min_score') || ''
  const safetyRating = searchParams.get('safety_rating') || ''
  const buyerType = searchParams.get('buyer_type') || ''
  const minUnits = searchParams.get('min_units') || ''
  const maxUnits = searchParams.get('max_units') || ''
  const ordering = searchParams.get('sort') || ''
  const unreadOnly = searchParams.get('unread') === '1'
  const followupOnly = searchParams.get('followup') === '1'
  const tagsFilter = searchParams.get('tags') || ''
  const excludeTagsFilter = searchParams.get('exclude_tags') || ''
  const eligibilityFilter = searchParams.get('eligibility') || ''

  const { inputValue, setInputValue } = useDebouncedInput(search, (value) => {
    updateParams({ search: value, page: undefined })
  })

  // The active content filters (no page/ordering) — shared by the list fetch and
  // the outbound-call and action automations, which freeze this exact set.
  const leadFilters: SalesLeadFilters = {
    search: search || undefined,
    status: statusFilter || undefined,
    expiringWithinDays: expiringDays ? Number(expiringDays) : undefined,
    notExpiringWithinDays: notExpiringDays ? Number(notExpiringDays) : undefined,
    assignedRep: assignedRep || undefined,
    source: (source as LeadSource) || undefined,
    state: stateFilter || undefined,
    minScore: minScore ? Number(minScore) : undefined,
    safetyRating: safetyRating || undefined,
    buyerType: buyerType ? (buyerType as BuyerType) : undefined,
    minUnits: minUnits ? Number(minUnits) : undefined,
    maxUnits: maxUnits ? Number(maxUnits) : undefined,
    unread: unreadOnly || undefined,
    followUp: followupOnly || undefined,
    tags: tagsFilter || undefined,
    excludeTags: excludeTagsFilter || undefined,
    eligibility: eligibilityFilter || undefined,
  }

  const { leads, count, hasNextPage, hasPreviousPage, isLoading } = useSalesLeads({
    page,
    ordering: ordering || undefined,
    ...leadFilters,
  })

  const { data: stats } = useSalesLeadStats()
  const { staffUsers } = useStaffUsers()
  const deleteLead = useDeleteLead()
  const canDelete = !isProduction

  const fromList = (location.state as { fromList?: boolean } | null)?.fromList === true

  const moreFilters: MoreFiltersState = {
    statusFilter,
    expiringDays,
    notExpiringDays,
    assignedRep,
    stateFilter,
    minScore,
    safetyRating,
    buyerType,
    minUnits,
    maxUnits,
    unreadOnly,
    followupOnly,
    tagIds: tagsFilter,
    excludeTagIds: excludeTagsFilter,
    eligibility: eligibilityFilter,
  }

  async function handleDelete(leadId: string, leadName: string) {
    try {
      await deleteLead.mutateAsync(leadId)
      toast.success(`Deleted lead "${leadName}"`)
      if (selectedId === leadId) {
        navigate('/insure/internal/sales-leads', { state: { fromList: true } })
      }
    } catch {
      toast.error('Failed to delete lead')
    }
  }

  function updateParams(updates: Record<string, string | undefined>) {
    const params: Record<string, string> = {}
    if (search && updates.search !== '') params.search = updates.search ?? search
    if (statusFilter && updates.status !== '') params.status = updates.status ?? statusFilter
    if (expiringDays && updates.expiring !== '') params.expiring = updates.expiring ?? expiringDays
    if (notExpiringDays && updates.not_expiring !== '')
      params.not_expiring = updates.not_expiring ?? notExpiringDays
    if (assignedRep && updates.rep !== '') params.rep = updates.rep ?? assignedRep
    if (source && updates.source !== '') params.source = updates.source ?? source
    if (stateFilter && updates.state !== '') params.state = updates.state ?? stateFilter
    if (minScore && updates.min_score !== '') params.min_score = updates.min_score ?? minScore
    if (safetyRating && updates.safety_rating !== '')
      params.safety_rating = updates.safety_rating ?? safetyRating
    if (buyerType && updates.buyer_type !== '') params.buyer_type = updates.buyer_type ?? buyerType
    if (minUnits && updates.min_units !== '') params.min_units = updates.min_units ?? minUnits
    if (maxUnits && updates.max_units !== '') params.max_units = updates.max_units ?? maxUnits
    if (ordering && updates.sort !== '') params.sort = updates.sort ?? ordering
    if (unreadOnly && updates.unread !== '') params.unread = updates.unread ?? '1'
    if (followupOnly && updates.followup !== '') params.followup = updates.followup ?? '1'
    if (tagsFilter && updates.tags !== '') params.tags = updates.tags ?? tagsFilter
    if (excludeTagsFilter && updates.exclude_tags !== '')
      params.exclude_tags = updates.exclude_tags ?? excludeTagsFilter
    if (eligibilityFilter && updates.eligibility !== '')
      params.eligibility = updates.eligibility ?? eligibilityFilter
    const pageNum = 'page' in updates ? Number(updates.page ?? 1) : page
    if (pageNum > 1) params.page = String(pageNum)

    Object.entries(updates).forEach(([key, value]) => {
      if (value === '') delete params[key]
      else if (value !== undefined) params[key] = value
    })

    setSearchParams(params, { state: location.state })
  }

  function openLead(leadId: string) {
    setListOpen(false)
    const qs = searchParams.toString()
    navigate(`/insure/internal/sales-leads/${leadId}${qs ? `?${qs}` : ''}`, {
      state: { fromList: true },
    })
  }

  function openLeadInNewTab(leadId: string) {
    window.open(`/insure/internal/sales-leads/${leadId}`, '_blank', 'noopener,noreferrer')
  }

  // Row click opens the lead; cmd/ctrl/shift-click and middle-click open the
  // detail in a new tab (rows aren't anchors, so we handle it explicitly).
  function handleRowClick(e: MouseEvent, leadId: string) {
    if (e.metaKey || e.ctrlKey || e.shiftKey) {
      openLeadInNewTab(leadId)
    } else {
      openLead(leadId)
    }
  }

  function toggleSort(field: string) {
    if (ordering === field) {
      updateParams({ sort: `-${field}`, page: undefined })
    } else if (ordering === `-${field}`) {
      updateParams({ sort: '', page: undefined })
    } else {
      updateParams({ sort: field, page: undefined })
    }
  }

  function renderSortIcon(field: string) {
    if (ordering === field) return <ArrowUp className="ml-1 inline size-3" />
    if (ordering === `-${field}`) return <ArrowDown className="ml-1 inline size-3" />
    return <ArrowUpDown className="ml-1 inline size-3 opacity-30" />
  }

  function goToPage(p: number) {
    updateParams({ page: p > 1 ? String(p) : undefined })
  }

  // Detail-only mode: URL was loaded directly (no list nav state).
  if (selectedId && !fromList) {
    return <SalesLeadDetail />
  }

  const splitView = !!selectedId

  const leadRail = (
    <>
      <div className="relative">
        <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <Input
          placeholder="Search leads..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          className="h-10 border-neutral-300 pl-9 text-sm"
        />
      </div>
      <StatusSelect
        value={statusFilter}
        onChange={(v) => updateParams({ status: v, page: undefined })}
        className="w-full"
      />
      <RepSelect
        value={assignedRep}
        staffUsers={staffUsers}
        onChange={(v) => updateParams({ rep: v, page: undefined })}
        className="w-full"
      />
      <OrderingSelect
        value={ordering}
        onChange={(v) => updateParams({ sort: v, page: undefined })}
        className="w-full"
      />
      <MoreFiltersDialog filters={moreFilters} staffUsers={staffUsers} updateParams={updateParams} />
      <div className="bg-surface flex min-h-0 flex-col overflow-y-auto rounded-md border border-neutral-200">
        {isLoading ? (
          <ul className="divide-y divide-neutral-200">
            {Array.from({ length: 8 }).map((_, i) => (
              <li key={i}>
                <CompactLeadCardSkeleton />
              </li>
            ))}
          </ul>
        ) : leads.length === 0 ? (
          <div className="text-muted-foreground p-4 text-center text-sm">No leads found</div>
        ) : (
          <ul className="divide-y divide-neutral-200">
            {leads.map((lead) => (
              <li key={lead.id}>
                <CompactLeadCard
                  lead={lead}
                  selected={lead.id === selectedId}
                  onClick={() => openLead(lead.id)}
                  onDelete={canDelete ? handleDelete : undefined}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
      {(hasPreviousPage || hasNextPage) && (
        <div className="text-muted-foreground flex items-center justify-between border-t pt-2 text-xs">
          <span>
            Page {page} ({count})
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={!hasPreviousPage}
              onClick={() => goToPage(page - 1)}
            >
              Prev
            </Button>
            <Button variant="outline" size="sm" disabled={!hasNextPage} onClick={() => goToPage(page + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </>
  )

  if (splitView) {
    return (
      <>
        <Helmet>
          <title>Sales Leads</title>
        </Helmet>
        <div className="flex min-h-0 flex-1 gap-4">
          <div className="flex w-[280px] shrink-0 flex-col gap-3 max-lg:hidden">{leadRail}</div>
          <div className="flex min-w-0 flex-1 flex-col gap-3 overflow-y-auto">
            <Sheet open={listOpen} onOpenChange={setListOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="w-fit lg:hidden">
                  <List className="size-3.5" />
                  All leads
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[88vw] sm:max-w-sm">
                <SheetHeader>
                  <SheetTitle>Leads</SheetTitle>
                </SheetHeader>
                <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 pb-6">{leadRail}</div>
              </SheetContent>
            </Sheet>
            <SalesLeadDetail />
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>Sales Leads</title>
      </Helmet>
      <div className="flex max-w-7xl flex-col gap-5">
        <div className="mb-2 flex flex-wrap items-center gap-3">
          <div className="text-2xl font-semibold">Sales Leads</div>
          <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
            <OutboundCallAutomationDialog filters={leadFilters} />
            <LeadActionAutomationDialog filters={leadFilters} />
            <IngestFromCabDialog staffUsers={staffUsers} />
            <IngestFromCsvDialog staffUsers={staffUsers} />
            <Link
              to={`/insure/internal/sales-leads/pipeline${
                searchParams.toString() ? `?${searchParams.toString()}` : ''
              }`}
            >
              <Button variant="outline" className="h-9">
                Pipeline view
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
            <Card>
              <CardContent>
                <div className="text-muted-foreground text-sm">Total Leads</div>
                <div className="mt-2 text-2xl font-bold">{stats.total}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <div className="text-muted-foreground text-sm">Cold</div>
                <div className="mt-2 text-2xl font-bold">{stats.byStatus.COLD || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <div className="text-muted-foreground text-sm">Expiring (30d)</div>
                <div className="mt-2 text-2xl font-bold text-orange-600">{stats.expiring30Days}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <div className="text-muted-foreground text-sm">Unassigned</div>
                <div className="mt-2 text-2xl font-bold">{stats.unassigned}</div>
              </CardContent>
            </Card>
          </div>
        )}

        <Card>
          <CardHeader className="px-4 sm:px-6">
            <div className="flex items-center justify-between">
              <CardTitle>Leads</CardTitle>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <div className="relative w-full sm:max-w-sm sm:flex-1">
                <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                <Input
                  placeholder="Search by name, DOT, or contact..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  className="border-neutral-300 pl-9 text-sm"
                />
              </div>

              <StatusSelect
                value={statusFilter}
                onChange={(v) => updateParams({ status: v, page: undefined })}
                className="w-full sm:w-48"
              />

              <RepSelect
                value={assignedRep}
                staffUsers={staffUsers}
                onChange={(v) => updateParams({ rep: v, page: undefined })}
                className="w-full sm:w-36"
              />

              {/* Only below lg, where the cards replace the table's sortable headers. */}
              <OrderingSelect
                value={ordering}
                onChange={(v) => updateParams({ sort: v, page: undefined })}
                className="w-full lg:hidden"
              />

              <MoreFiltersDialog filters={moreFilters} staffUsers={staffUsers} updateParams={updateParams} />
            </div>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            {isLoading ? (
              <LeadTableSkeleton canDelete={canDelete} />
            ) : (
              <>
                {/* A nine-column row has nowhere to go on a phone, so it becomes a card. */}
                <ul className="divide-y divide-neutral-200 lg:hidden">
                  {leads.length === 0 && (
                    <li className="text-muted-foreground py-5 text-center text-sm">No leads found</li>
                  )}
                  {leads.map((lead) => (
                    <li key={lead.id}>
                      <CompactLeadCard
                        lead={lead}
                        detailed
                        selected={false}
                        onClick={() => openLead(lead.id)}
                        onDelete={canDelete ? handleDelete : undefined}
                      />
                    </li>
                  ))}
                </ul>
                <div className="max-lg:hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="font-semibold">Company</TableHead>
                        <TableHead
                          className="cursor-pointer font-semibold select-none"
                          onClick={() => toggleSort('policy_expiration_date')}
                        >
                          Expires
                          {renderSortIcon('policy_expiration_date')}
                        </TableHead>
                        <TableHead
                          className="cursor-pointer font-semibold select-none"
                          onClick={() => toggleSort('power_units')}
                        >
                          Units
                          {renderSortIcon('power_units')}
                        </TableHead>
                        <TableHead
                          className="cursor-pointer font-semibold select-none"
                          onClick={() => toggleSort('conversion_score')}
                        >
                          Score
                          {renderSortIcon('conversion_score')}
                        </TableHead>
                        <TableHead className="font-semibold">Status</TableHead>
                        <TableHead className="font-semibold">Contact</TableHead>
                        <TableHead
                          className="cursor-pointer font-semibold select-none"
                          onClick={() => toggleSort('last_activity_at')}
                        >
                          Last Activity
                          {renderSortIcon('last_activity_at')}
                        </TableHead>
                        <TableHead className="font-semibold">Rep</TableHead>
                        {canDelete && <TableHead className="w-12" />}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leads.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={canDelete ? 9 : 8} className="pt-5 text-center">
                            No leads found
                          </TableCell>
                        </TableRow>
                      )}
                      {leads.map((lead) => {
                        const expiry = formatDaysUntilExpiration(lead.daysUntilExpiration)
                        return (
                          <TableRow
                            key={lead.id}
                            className={cn('cursor-pointer', lead.oosStatus && 'bg-red-50')}
                            onClick={(e) => handleRowClick(e, lead.id)}
                            onAuxClick={(e) => {
                              if (e.button === 1) {
                                e.preventDefault()
                                openLeadInNewTab(lead.id)
                              }
                            }}
                          >
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {lead.hasUnread && (
                                  <span
                                    className="size-2 shrink-0 rounded-full bg-blue-500"
                                    aria-label="Unread activity"
                                  />
                                )}
                                <span className="font-medium">{lead.legalName}</span>
                                <SequenceIndicator status={lead.sequenceStatus} />
                                {lead.oosStatus && (
                                  <Badge variant="destructive" className="text-xs">
                                    <AlertTriangle className="mr-1 size-3" />
                                    OOS
                                  </Badge>
                                )}
                              </div>
                              <div className="text-muted-foreground text-sm">
                                DOT: {lead.dotNumber} · {lead.state}
                              </div>
                            </TableCell>
                            <TableCell>
                              {expiry ? (
                                <span className={expiry.className}>{expiry.text}</span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell>{lead.powerUnits}</TableCell>
                            <TableCell>
                              <LeadScoreBadge score={lead.conversionScore} />
                            </TableCell>
                            <TableCell>
                              <Badge className={cn(salesLeadStatusColors[lead.status])}>
                                {salesLeadStatusLabels[lead.status]}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="text-muted-foreground">—</span>
                            </TableCell>
                            <TableCell>
                              <span className="text-muted-foreground text-sm">
                                {formatRelativeDate(lead.lastActivityAt, 'Never')}
                              </span>
                            </TableCell>
                            <TableCell>
                              {lead.assignedRepName ? (
                                <UserLabel name={lead.assignedRepName} src={lead.assignedRepAvatar} />
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            {canDelete && (
                              <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                <DeleteLeadButton
                                  leadId={lead.id}
                                  leadName={lead.legalName}
                                  onDelete={handleDelete}
                                />
                              </TableCell>
                            )}
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
                {(hasPreviousPage || hasNextPage) && (
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-muted-foreground text-sm">
                      Page {page} ({count} total)
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!hasPreviousPage}
                        onClick={() => goToPage(page - 1)}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!hasNextPage}
                        onClick={() => goToPage(page + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}
